import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/index.css";

import { AuthProvider, useAuth } from "@/lib/auth";
import { MembershipProvider, useMembership } from "@/lib/membership";
import { ensureServiceWorker } from "@/lib/push";
import Landing from "@/pages/Landing";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Contacts from "@/pages/Contacts";
import Family from "@/pages/Family";
import Alerts from "@/pages/Alerts";
import Settings from "@/pages/Settings";
import FindMe from "@/pages/FindMe";
import JoinInvite from "@/pages/JoinInvite";
import Billing from "@/pages/Billing";
import GeofenceEditor from "@/pages/GeofenceEditor";
import ManageDevices from "@/pages/ManageDevices";
import AcceptInvite from "@/pages/AcceptInvite";
import CommandCenter from "@/pages/CommandCenter";
import GroupDetail from "@/pages/GroupDetail";
import GroupPicker from "@/pages/GroupPicker";
import SignIn from "@/pages/SignIn";
import SignUp from "@/pages/SignUp";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PhoneContacts from "@/pages/PhoneContacts";
import ShareTarget from "@/pages/ShareTarget";
import ChildLinkModal from "@/components/ChildLinkModal";
import NetworkSentry from "@/components/NetworkSentry";
import NativeBootstrap from "@/components/NativeBootstrap";
import InstallPrompt from "@/components/InstallPrompt";
import InterstitialAd from "@/components/InterstitialAd";
import { API } from "@/lib/api";
import { useInactivityGate } from "@/lib/inactivity";
import useAndroidBackButton from "@/hooks/useAndroidBackButton";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  // Wall-clock inactivity gate — kicks the user back to /signin after 30 min
  // of no meaningful interaction. Does NOT delete the auth token so background
  // location tracking keeps working.
  useInactivityGate();
  if (location.state?.user) return children;
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-50 grid place-items-center">
        <div className="label-eyebrow">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  return children;
}

/** Nodes with active groups land on /group instead of /dashboard */
function NodeAwareDashboard() {
  const { nodeHasGroups, loaded } = useMembership();
  if (loaded && nodeHasGroups) return <Navigate to="/group" replace />;
  return <Dashboard />;
}

function EliteOnly({ children }) {
  const { isEliteMaster, loaded } = useMembership();
  if (!loaded) {
    return <div className="min-h-screen bg-zinc-950 text-zinc-50 grid place-items-center"><div className="label-eyebrow">Loading…</div></div>;
  }
  if (!isEliteMaster) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();
  // Android hardware back button — always mount inside <BrowserRouter>,
  // once, so overlay-close / history-pop / exit-app behavior is consistent.
  useAndroidBackButton();

  // First-launch resume: if we're now running as an installed PWA and a
  // pending invite token is sitting in localStorage from before install,
  // navigate straight to the invite. This makes the "install → open →
  // continue where you left off" experience seamless.
  React.useEffect(() => {
    try {
      const isStandalone =
        window.matchMedia?.("(display-mode: standalone)").matches ||
        window.navigator.standalone === true;
      if (!isStandalone) return;
      if (window.__proximityInviteResumed) return;
      const raw = localStorage.getItem("pending_invite");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const token = parsed?.token;
      if (!token) return;
      // Only resume from the root/landing/signin — never yank someone
      // out of the middle of their session.
      // Auth pages already carry an explicit `next` destination. Never
      // pull someone out of sign-in/sign-up to reopen the same invite.
      if (location.pathname === "/") {
        window.__proximityInviteResumed = true;
        // Fire the "install completed → invite auto-resumed" funnel beacon.
        // Uses sendBeacon so it survives even if the navigation is fast.
        try {
          const url = `${API}/analytics/invite-resumed`;
          const body = JSON.stringify({ invite_token: token, source: "pwa_boot" });
          if (navigator.sendBeacon) {
            navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
          } else {
            fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true });
          }
        } catch { /* beacon best-effort */ }
        navigate(`/join/${encodeURIComponent(token)}`, { replace: true });
      }
    } catch { /* non-fatal */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Handle Google OAuth callback (session_id in URL hash) synchronously
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/signin" element={<SignIn />} />
      <Route path="/signup" element={<SignUp />} />
      <Route path="/forgot" element={<ForgotPassword />} />
      <Route path="/reset/:token" element={<ResetPassword />} />
      <Route path="/dashboard" element={<ProtectedRoute><NodeAwareDashboard /></ProtectedRoute>} />
      <Route path="/contacts" element={<ProtectedRoute><Contacts /></ProtectedRoute>} />
      <Route path="/phonebook" element={<ProtectedRoute><PhoneContacts /></ProtectedRoute>} />
      <Route path="/share-target" element={<ProtectedRoute><ShareTarget /></ProtectedRoute>} />
      <Route path="/family" element={<ProtectedRoute><Family /></ProtectedRoute>} />
      <Route path="/alerts" element={<ProtectedRoute><Alerts /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/find/:id" element={<ProtectedRoute><FindMe /></ProtectedRoute>} />
      <Route path="/join/:token" element={<JoinInvite />} />
      <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
      <Route path="/devices" element={<ProtectedRoute><ManageDevices /></ProtectedRoute>} />
      <Route path="/accept/:code" element={<AcceptInvite />} />
      <Route path="/geofence/:contactId" element={<ProtectedRoute><GeofenceEditor /></ProtectedRoute>} />
      <Route path="/hub" element={<ProtectedRoute><EliteOnly><CommandCenter /></EliteOnly></ProtectedRoute>} />
      <Route path="/group" element={<ProtectedRoute><GroupPicker /></ProtectedRoute>} />
      <Route path="/groups/:id" element={<ProtectedRoute><GroupDetail /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  useEffect(() => { ensureServiceWorker(); }, []);
  return (
    <AuthProvider>
      <MembershipProvider>
        <BrowserRouter>
          <NetworkSentry>
            <div className="app-shell pt-safe-top pb-safe-bottom min-h-screen">
              <AppRouter />
              <InstallPrompt />
              <InterstitialAd />
              <ChildLinkModal />
              <NativeBootstrap />
              <Toaster theme="dark" position="top-center" richColors closeButton />
            </div>
          </NetworkSentry>
        </BrowserRouter>
      </MembershipProvider>
    </AuthProvider>
  );
}
